import type { LeaveRequest, LeaveType } from "api/leave/Leave.typed";
import type { LeaveMotifVisibilityOpts } from "utils/planningLeaveHelpers";
import {
  formatPlanningUserShortName,
  formatPlanningLeaveCardTooltipHeader,
  formatPlanningLeaveCardTooltipBody,
  isPlanningLeaveCompactSlot,
  isPlanningLeaveCrossTenantSlot,
  isPlanningGlobalUnavailabilityLeaveRequest,
} from "utils/planningLeaveHelpers";
import { PlanningSlotTooltipWrap } from "./PlanningTooltip";
import {
  PlanningDayChip,
  PLANNING_LEAVE_EMOJI,
  PLANNING_UNAVAILABLE_EMOJI,
  PLANNING_LEAVE_CHIP_BG,
} from "./PlanningDayChip";

type DayLeaveChipProps = {
  request: LeaveRequest;
  dayKey: string;
  typesMap: Record<number, { Color: string; Name: string }>;
  getUserName?: (id: number) => string | undefined;
  viewerTenantCompanyId?: number;
  companyTimeZone?: string | null;
  leaveTypes?: LeaveType[];
  leaveMotifVisibilityOpts: LeaveMotifVisibilityOpts;
};

export default function DayLeaveChip({
  request,
  dayKey,
  typesMap,
  getUserName,
  viewerTenantCompanyId,
  companyTimeZone,
  leaveTypes,
  leaveMotifVisibilityOpts,
}: DayLeaveChipProps) {
  const cross = isPlanningLeaveCrossTenantSlot(request);
  const compact = isPlanningLeaveCompactSlot(request, viewerTenantCompanyId, leaveTypes);
  const nameRaw = request.user_name?.trim() || getUserName?.(request.user_id)?.trim();
  const chipText = formatPlanningUserShortName(nameRaw);

  const isUnavailability = isPlanningGlobalUnavailabilityLeaveRequest(request, leaveTypes);
  const leaveChipBg = cross || compact ? undefined : PLANNING_LEAVE_CHIP_BG;
  const leaveEmoji = isUnavailability ? PLANNING_UNAVAILABLE_EMOJI : PLANNING_LEAVE_EMOJI;

  const tooltipHeader = formatPlanningLeaveCardTooltipHeader(
    request,
    getUserName,
    viewerTenantCompanyId,
    leaveTypes,
  );
  const tooltipBody = formatPlanningLeaveCardTooltipBody(
    request,
    typesMap,
    getUserName,
    companyTimeZone,
    viewerTenantCompanyId,
    leaveTypes,
    leaveMotifVisibilityOpts,
  );

  return (
    <PlanningSlotTooltipWrap
      tooltipId={`plan-lr-${request.id}-${dayKey}`}
      tooltipText={tooltipBody}
      tooltipHeader={tooltipHeader}
    >
      <PlanningDayChip emoji={leaveEmoji} label={chipText} bgColor={leaveChipBg} />
    </PlanningSlotTooltipWrap>
  );
}
